import * as _ from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export type TeammatesFilterType = 'ADMIN' | 'GUEST' | 'USER';
export type TeammatesFilterStatus =
  | 'ACTIVE'
  | 'DEACTIVATED'
  | 'INVITED'
  | 'PENDING'
  | 'REJECTED';

const DEFAULT_TEAMMATES_TYPE: TeammatesFilterType[] = [];
const DEFAULT_TEAMMATES_STATUS: TeammatesFilterStatus[] = ['ACTIVE'];

const TYPES_KEY = 'types[]';
const STATUSES_KEY = 'statuses[]';
const SEPARATOR = '|';

const useTeammatesFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { types, statuses } = useMemo(() => {
    return {
      types: searchParams.has(TYPES_KEY)
        ? (searchParams
            .get(TYPES_KEY)
            ?.split(SEPARATOR)
            .filter(_.identity) as TeammatesFilterType[])
        : DEFAULT_TEAMMATES_TYPE,
      statuses: searchParams.has(STATUSES_KEY)
        ? (searchParams
            .get(STATUSES_KEY)
            ?.split(SEPARATOR)
            .filter(_.identity) as TeammatesFilterStatus[])
        : DEFAULT_TEAMMATES_STATUS,
    };
  }, [searchParams]);

  const updateTypes = useCallback(
    (newTypes: TeammatesFilterType[]) => {
      if (_.isEqual(newTypes, types)) return;

      if (_.isEqual(newTypes, DEFAULT_TEAMMATES_TYPE)) {
        searchParams.delete(TYPES_KEY);
      } else {
        searchParams.set(TYPES_KEY, newTypes.join(SEPARATOR));
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, types],
  );

  const updateStatuses = useCallback(
    (newStatuses: TeammatesFilterStatus[]) => {
      if (_.isEqual(newStatuses, statuses)) return;

      if (newStatuses.length === 0) {
        searchParams.set(STATUSES_KEY, '');
      } else if (_.isEqual(newStatuses, DEFAULT_TEAMMATES_STATUS)) {
        searchParams.delete(STATUSES_KEY);
      } else {
        searchParams.set(STATUSES_KEY, newStatuses.join(SEPARATOR));
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, statuses],
  );

  const reset = useCallback(() => setSearchParams({}), [setSearchParams]);

  return {
    types,
    statuses,
    updateTypes,
    updateStatuses,
    reset,
  };
};

export default useTeammatesFilter;
